import React, {useMemo, useEffect} from 'react';
import {makeStyles} from 'tss-react/mui';
import {FormattedMessage, useIntl} from 'react-intl';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ProgressStepper from '../components/ProgressStepper';
import FloatingHarHabituchCta from '../components/FloatingHarHabituchCta';
import DocumentCard from '../components/DocumentCard';
import InsuranceHistorySection from '../components/InsuranceHistorySection';
import {useDocumentCollection} from '../context/DocumentCollectionContext';
import {SCREENS} from '../constants/documentCollection';
import {deriveDocuments, formatPeriodDate} from '../utils/documentHelpers';
import {getSingleInsuranceHistoryStatus, getDocumentCollectionActiveStep} from '../utils/documentStatus';
import useDocumentUploads from '../hooks/useDocumentUploads';

const useStyles = makeStyles()((theme) => ({
  container: {
    maxWidth: 600,
    margin: '0 auto',
    padding: '0 16px calc(100px + env(safe-area-inset-bottom, 0px))',
    textAlign: 'left',
    '@media (min-width: 768px)': {
      padding: '0 24px calc(100px + env(safe-area-inset-bottom, 0px))',
    },
  },
  sectionTitle: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 700,
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.primary,
    textAlign: 'center',
    marginBottom: 8,
  },
  sectionDescription: {
    fontSize: theme.typography.pxToRem(14),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    lineHeight: 1.6,
    marginBottom: 20,
  },
  cancellationBanner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px 20px',
    backgroundColor: theme.palette.warning.main,
    borderRadius: 14,
    marginBottom: 20,
    textAlign: 'center',
  },
  cancellationBannerIcon: {
    fontSize: theme.typography.pxToRem(24),
    color: theme.palette.error.main,
    marginBottom: 6,
  },
  cancellationBannerTitle: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.primary,
    marginBottom: 4,
  },
  cancellationBannerSubtitle: {
    fontSize: theme.typography.pxToRem(14),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.primary,
  },
  documentsSection: {
    marginBottom: 24,
  },
}));

const UploadScreen = () => {
  const {classes} = useStyles();
  const intl = useIntl();
  const {
    token, protectionNumber, yearsRequired, statuses,
    uploadedFiles, uploadingDocs, setUploadedFile, removeUploadedFile,
    setScreen, setDocumentStatus, setDocUploading,
    insuranceHistoryPath, setInsuranceHistoryPath, idIssuanceDateProvided,
    futureCancellationDaysLeft,
  } = useDocumentCollection();

  // Derive documents from raw state
  const {baseDocs, insuranceHistoryDocs} = useMemo(
    () => deriveDocuments({protectionNumber, yearsRequired, statuses}, intl),
    [protectionNumber, yearsRequired, statuses, intl],
  );

  // Upload handlers
  const {
    batchErrors,
    singleHistoryFiles,
    handleFileSelected,
    handleInsuranceFileSelected,
    handleBatchSubmit,
    handleSingleHistoryFile,
  } = useDocumentUploads({
    token,
    uploadedFiles,
    insuranceHistoryDocs,
    setUploadedFile,
    removeUploadedFile,
    setDocumentStatus,
    setDocUploading,
  });

  // Derived statuses
  const yearsRange = useMemo(() => {
    if (insuranceHistoryDocs.length === 0) return '';
    const firstStart = insuranceHistoryDocs[0].periodStart;
    const lastEnd = insuranceHistoryDocs[insuranceHistoryDocs.length - 1].periodEnd;
    return `${formatPeriodDate(firstStart)} - ${formatPeriodDate(lastEnd)}`;
  }, [insuranceHistoryDocs]);

  const singleHistoryStatus = useMemo(
    () => getSingleInsuranceHistoryStatus(insuranceHistoryDocs),
    [insuranceHistoryDocs],
  );

  const activeStep = useMemo(
    () => getDocumentCollectionActiveStep({baseDocs, insuranceHistoryDocs, idIssuanceDateProvided}),
    [baseDocs, insuranceHistoryDocs, idIssuanceDateProvided],
  );

  // Auto-advance to Success when all documents are approved
  useEffect(() => {
    if (activeStep === 3) {
      setScreen(SCREENS.SUCCESS);
    }
  }, [activeStep, setScreen]);

  const openHarHabituch = () => {
    setScreen(SCREENS.HAR_HABITUCH);
  };

  return (
    <div>
      <ProgressStepper activeStep={activeStep} />

      <div className={classes.container}>
        <div className={classes.sectionTitle}>
          <FormattedMessage id='docCollection.upload.title' />
        </div>
        <div className={classes.sectionDescription}>
          <FormattedMessage id='docCollection.upload.description' />
        </div>

        {futureCancellationDaysLeft != null && futureCancellationDaysLeft <= 7 && (
          <div className={classes.cancellationBanner}>
            <WarningAmberIcon className={classes.cancellationBannerIcon} />
            <div className={classes.cancellationBannerTitle}>
              <FormattedMessage id='docCollection.upload.cancellationBanner.title' values={{days: futureCancellationDaysLeft}} />
            </div>
            <div className={classes.cancellationBannerSubtitle}>
              <FormattedMessage id='docCollection.upload.cancellationBanner.subtitle' />
            </div>
          </div>
        )}

        {/* Base documents: license + protection (if needed) */}
        <div className={classes.documentsSection}>
          {baseDocs.map((doc) => (
            <DocumentCard
              key={doc.id}
              document={doc}
              uploadedFiles={uploadedFiles[doc.id] || []}
              onFileSelected={handleFileSelected}
              onRemoveFile={removeUploadedFile}
              uploading={!!uploadingDocs[doc.id]}
            />
          ))}
        </div>

        {/* Insurance history section */}
        {insuranceHistoryDocs.length > 0 && (
          <InsuranceHistorySection
            insuranceHistoryDocs={insuranceHistoryDocs}
            idIssuanceDateProvided={idIssuanceDateProvided}
            insuranceHistoryPath={insuranceHistoryPath}
            setInsuranceHistoryPath={setInsuranceHistoryPath}
            uploadedFiles={uploadedFiles}
            uploadingDocs={uploadingDocs}
            batchErrors={batchErrors}
            singleHistoryFiles={singleHistoryFiles}
            singleHistoryStatus={singleHistoryStatus}
            yearsRange={yearsRange}
            onOpenHarHabituch={openHarHabituch}
            onInsuranceFileSelected={handleInsuranceFileSelected}
            onBatchSubmit={handleBatchSubmit}
            onSingleHistoryFileSelected={handleSingleHistoryFile}
            onRemoveFile={removeUploadedFile}
          />
        )}
      </div>

      {/* Floating Har HaBituach CTA */}
      {!idIssuanceDateProvided && insuranceHistoryDocs.length > 0 && (
        <FloatingHarHabituchCta onClick={openHarHabituch} />
      )}
    </div>
  );
};

export default UploadScreen;
